import type { CreateNewCallDto } from './generated/Api';

/**
 * The words both apps show when a call is created or edited, so the
 * dashboard and the phone offer the same tow types and the same reasons.
 * Each app kept its own copy, and the phone had neither.
 */

/** The rate tier a call is charged at, as the create routes take it. */
export type TowType = NonNullable<CreateNewCallDto['towType']>;

/*
 * Keyed by the generated type, so a tow type the backend adds does not
 * compile until it has words here, and one it drops cannot linger.
 */
const TOW_TYPE_WORDS: Record<TowType, string> = {
  ANY: 'Any',
  LIGHT: 'Light',
  MEDIUM: 'Medium',
  HEAVY: 'Heavy',
  CLASS_D: 'Class D',
  MOTORCYCLE: 'Motorcycle',
  TRAILER: 'Trailer',
  TRACTOR: 'Tractor',
  EQUIPMENT: 'Equipment',
  CONTAINER: 'Container',
  MATERIAL: 'Material',
  OTHER: 'Other',
};

/** Every tow type, in the order a picker lists them: Any first. */
export const TOW_TYPES = Object.keys(TOW_TYPE_WORDS) as [TowType, ...TowType[]];

/** "Class D" for CLASS_D. */
export const formatTowTypeForDisplay = (towType: TowType): string =>
  TOW_TYPE_WORDS[towType];

/**
 * The reasons a call is offered. Not a closed set: the server stores
 * whatever reason it is given, so both apps also take one typed in.
 */
export const CALL_REASONS = [
  'Accident',
  'Abandoned Vehicle',
  'Expired Plates',
  'Fire Lane Parking',
  'Fuel Delivery',
  'GOA',
  'Handicap Parking',
  'Illegal Parking',
  'Inoperable Vehicle',
  'Jump Start',
  'Lockout',
  'Private Property Tow',
  'Repossession',
  'Roadside Assistance',
  'Tow - Accident',
  'Tow - Breakdown',
  'Transport',
  'Winch Out',
] as const;

export type CallReason = (typeof CALL_REASONS)[number];
